import React, { useContext, useRef, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { CardSubtitle } from '@progress/kendo-react-layout';
import { Col, Row } from 'react-bootstrap';
import { dataURItoBlob, loadImageToBlobUrl } from '../../utils/image-helper';
import Webcam from 'react-webcam';
import { FeedbackDocumentContext } from '../../contexts/feedback-document';
import { DisplayLanguageFeedbackContext } from '../../contexts/display-languages-feedback';
import { generateTitleById } from '../../utils/game-document/display-languages';

type Props = {
  isReadonly?: boolean;
  selectedImage?: string;
  onSelectedImageCallback?: (blobUrl: string | File, size?: number) => void;
  formId: string;
  index: number;
};

const AnswerImage = ({
  isReadonly = false,
  selectedImage = '',
  onSelectedImageCallback = () => {},
  formId,
  index
}: Props) => {
  const [feedbackDocument] = useContext(FeedbackDocumentContext);
  const [feedbackDisplayLangContext] = useContext(
    DisplayLanguageFeedbackContext
  );
  const videoRef = useRef<Webcam>(null);
  const inputFile = useRef<HTMLInputElement>(null);
  const [blobImage, setBlobImage] = useState<string>(selectedImage);
  const [isDisplayWebcam, setIsDisplayWebcam] = useState<boolean>(false);
  const [imageExtension, setImageExtension] = useState<string>('');
  const [fileUpload, setFileUpload] = useState<File>();
  const [isLargeImageSize, setIsLargeImageSize] = useState<boolean>(false);

  const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const blobUrl = loadImageToBlobUrl(file);
      const fileName = file.name;
      const imageExt = fileName.slice(fileName.lastIndexOf('.'));
      const newFileName = `${feedbackDocument.feedbackDocument?.playerName}-Question${index}-${Date.now()}${imageExt}`;

      const sizeImage = e.target.files![0].size;
      const maxImageSize = 512000; //max image size in byte
      if (sizeImage > maxImageSize) {
        setIsLargeImageSize(true);
      } else {
        setIsLargeImageSize(false);
      }

      if (imageExt) {
        setImageExtension(imageExt);
      }
      setBlobImage(blobUrl);
      const newFile = new File([file], newFileName, { type: file.type });
      onSelectedImageCallback(newFile, file.size ?? 0);
      setFileUpload(newFile);
    }
  };

  const onAddImageClick = () => inputFile.current && inputFile.current.click();

  const turnOnCameraClick = () => setIsDisplayWebcam(true);

  const turnOffCameraClick = () => {
    const { stream } = videoRef.current!;
    const tracks = stream?.getTracks();
    tracks?.forEach((item) => item.stop());
    setBlobImage('');
    onSelectedImageCallback('');
    setIsDisplayWebcam(false);
  };

  const onTakePictureClickHandler = () => {
    const screenShoot = videoRef.current?.getScreenshot();
    if (screenShoot && screenShoot !== '') {
      fetch(screenShoot)
        .then((result) => {
          const imageFile = dataURItoBlob(result.url);
          let myFile = imageFile;
          if (imageFile instanceof Blob) {
            const extension = imageFile.type.split('/')[1];
            myFile = new File(
              [imageFile],
              `${feedbackDocument.feedbackDocument?.playerName}-Question${index}-${Date.now()}.${extension}`,
              {
                type: imageFile.type
              }
            );
          }
          onSelectedImageCallback(myFile as File, imageFile.size);
          setBlobImage(result.url);
        })
        .catch((error) => console.error(error))
        .finally(() => setIsDisplayWebcam(false));
    }
  };

  const getFileExtension = (filename: string) => {
    // get file extension
    const extension = filename.substring(
      filename.lastIndexOf('.') + 1,
      filename.length
    );
    return extension;
  };

  const allowedImages = () => {
    return (
      getFileExtension(selectedImage).toLocaleLowerCase() === 'png' ||
      getFileExtension(selectedImage).toLocaleLowerCase() === 'jpg' ||
      getFileExtension(selectedImage).toLocaleLowerCase() === 'jpeg' ||
      getFileExtension(selectedImage).toLocaleLowerCase() === 'gif' ||
      getFileExtension(selectedImage) === ''
    );
  };

  return (
    <div id={'answer-image'}>
      <Row className={'py-2'}>
        <Col>
          <div className={'border text-center h-100'}>
            {isDisplayWebcam ? (
              <Webcam
                id={'answer-webcam'}
                className={'py-2'}
                audio={false}
                videoConstraints={{
                  height: 480,
                  width: 480,
                  facingMode: 'user'
                }}
                screenshotFormat={'image/jpeg'}
                autoPlay={true}
                ref={videoRef}></Webcam>
            ) : (
              <>
                <div
                  className={
                    'd-flex justify-content-center align-items-center py-5'
                  }>
                  {blobImage === '' || selectedImage === '' ? (
                    <span
                      className={
                        'material-symbols-outlined text-success text-size-64'
                      }>
                      wallpaper
                    </span>
                  ) : allowedImages() ? (
                    <img
                      src={selectedImage ?? blobImage}
                      height={300}
                      alt={'Preview'}
                    />
                  ) : (
                    <p>
                      <span
                        className={
                          'material-symbols-outlined text-primary text-size-64'
                        }>
                        cloud_done
                      </span>
                      <br />
                      {!isReadonly ? (
                        (fileUpload?.name ??
                        decodeURI(
                          selectedImage.substring(
                            selectedImage.lastIndexOf('/') + 1
                          )
                        ))
                      ) : (
                        <>
                          <span>
                            {decodeURI(
                              selectedImage.substring(
                                selectedImage.lastIndexOf('/') + 1
                              )
                            )}
                          </span>{' '}
                          <br />
                          <a href={selectedImage}>Download</a>
                        </>
                      )}
                    </p>
                  )}
                </div>
                {blobImage === '' && (
                  <CardSubtitle>
                    {generateTitleById(
                      '567963b7-47c0-40e9-8659-2e43a5a5befb',
                      feedbackDocument,
                      feedbackDisplayLangContext.displayLanguageSelected
                        .resources!,
                      'feedback'
                    ) || 'Preview'}
                  </CardSubtitle>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
      {/* currently commented as requested from User Story #5365 */}
      {/* {isLargeImageSize && (
        <Row>
          <Error>Please reduce your file size below 500 Kb</Error>
        </Row>
      )} */}
      {!isReadonly && (
        <Row className={'py-2'}>
          <Col md={6}>
            <Button
              className={'w-100 upload-button p-2 mb-2'}
              onClick={
                isDisplayWebcam ? onTakePictureClickHandler : onAddImageClick
              }>
              <div
                className={
                  'd-flex justify-content-center align-items-center gap-3'
                }>
                <span
                  className={'material-symbols-outlined mr-2 text-success'}
                  role={'button'}>
                  add_photo_alternate
                </span>
                <span>
                  {isDisplayWebcam
                    ? generateTitleById(
                        '7b63892e-236c-4b6a-bfec-d5d3d66794af',
                        feedbackDocument,
                        feedbackDisplayLangContext.displayLanguageSelected
                          .resources!,
                        'feedback'
                      ) || 'Take picture'
                    : generateTitleById(
                        'b6d7d97b-37bd-4ef9-aa9e-0e02a404a375',
                        feedbackDocument,
                        feedbackDisplayLangContext.displayLanguageSelected
                          .resources!,
                        'feedback'
                      ) || 'Add from files'}
                </span>
              </div>
            </Button>
            <input
              type={'file'}
              id={'answer-file-upload'}
              className={'d-none'}
              onChange={handleSelectImage}
              onClick={(event) => {
                event.currentTarget.value = '';
              }}
              ref={inputFile}
              accept={'image/*'}
            />
          </Col>
          <Col md={6}>
            <Button
              className={'w-100 upload-button p-2 mb-2'}
              onClick={
                isDisplayWebcam ? turnOffCameraClick : turnOnCameraClick
              }>
              <div
                className={
                  'd-flex justify-content-center align-items-center gap-3'
                }>
                <span
                  className={'material-symbols-outlined mr-2 text-success'}
                  role={'button'}>
                  add_a_photo
                </span>
                <span>
                  {isDisplayWebcam
                    ? generateTitleById(
                        '59473aee-f6c3-4657-aa6e-f72a9726e4dc',
                        feedbackDocument,
                        feedbackDisplayLangContext.displayLanguageSelected
                          .resources!,
                        'feedback'
                      ) || 'Turn off camera'
                    : generateTitleById(
                        '51689c93-1e57-4c72-9308-5deab301614a',
                        feedbackDocument,
                        feedbackDisplayLangContext.displayLanguageSelected
                          .resources!,
                        'feedback'
                      ) || 'Take from camera'}
                </span>
              </div>
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default AnswerImage;
